/**
 * Registers /sw.js (production builds only) so vs-computer play works offline
 * after the first visit, and makes sure a new deploy actually lands.
 *
 * A new deploy installs a new worker that takes over right away. The page
 * itself is only as new as the bundle it loaded, though: an installed iPhone
 * app is usually resumed from memory rather than relaunched, so it can keep
 * running an old bundle for days. So the page also asks the Worker which
 * bundle is current (`/api/build`, never cached, never touched by the service
 * worker) at launch and every time it comes back to the foreground. A stale
 * page reloads once, at a safe moment:
 *
 * - on the home screen, at once;
 * - in a vs-computer game (saved after every move), while the app is out of
 *   sight or right as it comes back (the same as a fresh launch);
 * - never during an online game or the tutorial: there it waits for home.
 */
import { useEffect, useRef, useState } from 'react';
import type { Route } from './router';

/** A resume this recent counts as a launch: a reload then looks like the app opening. */
export const RESUME_WINDOW_MS = 6000;
const RELOAD_KEY = 'ancientnine.reloadedFor';

export interface DeployedBuild {
  main: string | null;
  version: string | null;
}

let updateReady = false;
let reloadTarget = 'sw';
let resumedAt = Date.now();
let deployed: DeployedBuild | null = null;
const listeners = new Set<() => void>();

function markReady(target: string): void {
  reloadTarget = target;
  updateReady = true;
  listeners.forEach((fn) => fn());
}

/** The main bundle this page is running (`/static/main-<hash>.js`), from its own script tag. */
export function pageBundle(doc: Pick<Document, 'querySelectorAll'> | null = typeof document === 'undefined' ? null : document): string | null {
  if (!doc) return null;
  for (const el of Array.from(doc.querySelectorAll('script[src]'))) {
    const src = el.getAttribute('src') ?? '';
    const m = /\/static\/main-[^/?#]+\.js/.exec(src);
    if (m) return m[0];
  }
  return null;
}

/** Is this page's bundle older than the deployed one? Null when either side is unknown. */
export function isStale(mine: string | null, latest: DeployedBuild | null): boolean | null {
  if (!mine || !latest?.main) return null;
  return latest.main !== mine;
}

/** Ask the Worker what is deployed. 'unknown' offline or on any error. */
export async function checkForUpdate(fetcher: typeof fetch = fetch): Promise<'stale' | 'current' | 'unknown'> {
  try {
    const res = await fetcher('/api/build', { cache: 'no-store' });
    if (!res.ok) return 'unknown';
    const b = (await res.json()) as DeployedBuild;
    deployed = { main: b.main ?? null, version: b.version ?? null };
    const stale = isStale(pageBundle(), deployed);
    if (stale === null) return 'unknown';
    if (stale) markReady(deployed.main!);
    return stale ? 'stale' : 'current';
  } catch {
    return 'unknown';
  }
}

export interface ReloadMoment {
  route: Route['name'];
  hidden: boolean;
  /** Milliseconds since the page loaded or last came back to the foreground. */
  sinceResume: number;
  /** Already reloaded once for this build (never loop). */
  already: boolean;
}

/** May a stale page reload right now? */
export function safeToReload(m: ReloadMoment): boolean {
  if (m.already) return false;
  if (m.route === 'home') return true;
  if (m.route !== 'solo') return false; // online rooms and the tutorial wait for home
  return m.hidden || m.sinceResume < RESUME_WINDOW_MS;
}

function reloadedFor(): string | null {
  try {
    return sessionStorage.getItem(RELOAD_KEY);
  } catch {
    return null;
  }
}

function reloadOnce(target: string): void {
  try {
    sessionStorage.setItem(RELOAD_KEY, target);
  } catch {
    /* no session storage: the stale check after reload finds the page current anyway */
  }
  location.reload();
}

export function registerServiceWorker(): void {
  if (!import.meta.env.PROD || typeof navigator === 'undefined' || !('serviceWorker' in navigator)) return;
  const sw = navigator.serviceWorker;
  // Only an update counts: the first install also fires controllerchange (clients.claim).
  let hadController = !!sw.controller;
  sw.addEventListener('controllerchange', () => {
    if (hadController) {
      // A new worker took over. The page may already be running the new bundle (it loaded fresh HTML);
      // reload only if it is stale, or if we cannot tell.
      void checkForUpdate().then((r) => {
        if (r === 'unknown') markReady('sw');
      });
    }
    hadController = true;
  });
  const register = () => {
    sw.register('/sw.js').then(
      (reg) => {
        // Long-lived home-screen apps: look for a new build whenever the app comes back.
        document.addEventListener('visibilitychange', () => {
          if (document.visibilityState === 'visible') reg.update().catch(() => {});
        });
      },
      () => {},
    );
  };
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') {
      resumedAt = Date.now();
      void checkForUpdate();
    }
  });
  void checkForUpdate();
  if (document.readyState === 'complete') register();
  else window.addEventListener('load', register, { once: true });
}

/** Apply an update at a safe moment (see the top of this file); otherwise say so once. */
export function useServiceWorkerUpdates(route: Route, notify: (text: string) => void): void {
  const told = useRef(false);
  const routeRef = useRef(route);
  routeRef.current = route;
  useEffect(() => {
    const check = () => {
      if (!updateReady) return;
      const moment: ReloadMoment = {
        route: routeRef.current.name,
        hidden: document.visibilityState === 'hidden',
        sinceResume: Date.now() - resumedAt,
        already: reloadedFor() === reloadTarget,
      };
      if (safeToReload(moment)) {
        reloadOnce(reloadTarget);
      } else if (!told.current && !moment.already && moment.route !== 'home') {
        told.current = true;
        notify(
          moment.route === 'solo'
            ? 'Ancient Nine was updated. The new version loads the next time you open the app; your game is saved.'
            : 'Ancient Nine was updated. The new version loads when you’re back on the home screen.',
        );
      }
    };
    listeners.add(check);
    check();
    const onHide = () => {
      if (document.visibilityState === 'hidden') check();
    };
    document.addEventListener('visibilitychange', onHide);
    return () => {
      listeners.delete(check);
      document.removeEventListener('visibilitychange', onHide);
    };
  }, [route, notify]);
}

/** Ask the controlling service worker for its build VERSION (null without one, or no answer). */
export function serviceWorkerVersion(timeoutMs = 1500): Promise<string | null> {
  const sw = typeof navigator === 'undefined' ? undefined : navigator.serviceWorker;
  const ctl = sw?.controller;
  if (!sw || !ctl) return Promise.resolve(null);
  return new Promise((resolve) => {
    const done = (v: string | null) => {
      sw.removeEventListener('message', onMsg);
      clearTimeout(timer);
      resolve(v);
    };
    const onMsg = (e: MessageEvent) => {
      const v = (e.data as { version?: unknown } | null)?.version;
      if (typeof v === 'string') done(v);
    };
    const timer = setTimeout(() => done(null), timeoutMs);
    sw.addEventListener('message', onMsg);
    ctl.postMessage('version');
  });
}

/** "00262d1561f3", or the page bundle's own hash without a service worker; plus whether an update is waiting. */
export function buildLabel(swVersion: string | null, mine: string | null, latest: DeployedBuild | null): string | null {
  const own = mine ? /main-([^.]+)\.js$/.exec(mine)?.[1] ?? null : null;
  const id = swVersion ?? own;
  if (!id) return null;
  return isStale(mine, latest) ? `${id} · update ready` : id;
}

/** The build this device is running, for How to Play (so a phone can say which deploy it has). */
export function useBuildLabel(): string | null {
  const [label, setLabel] = useState<string | null>(() => buildLabel(null, pageBundle(), deployed));
  useEffect(() => {
    let live = true;
    void serviceWorkerVersion().then((v) => {
      if (live) setLabel(buildLabel(v, pageBundle(), deployed));
    });
    return () => {
      live = false;
    };
  }, []);
  return label;
}
